/**
 * Referral attribution helpers (frontend).
 *
 * The lifecycle:
 *
 *   1. A visitor lands on the app with `?ref=ABC123` in the URL.
 *   2. We:
 *      - validate the code with the backend (no auth, public endpoint),
 *      - persist it to localStorage AND a 90-day cookie so it survives page
 *        navigation, refresh, and even tab close,
 *      - record a click row server-side and remember the returned clickId
 *        (so we can mark that exact click as "converted" later),
 *      - strip `?ref=` from the URL so subsequent shares from this user
 *        don't accidentally re-attribute their own visitors.
 *   3. At signup the form reads the persisted code + clickId and sends them
 *      with the register payload. The backend resolves them at /verify.
 *   4. On any successful login OR after signup, we clear the persisted
 *      attribution (it's been used).
 *
 * The cookie is auxiliary — the server-side trust comes from the explicit
 * referralCode field at signup time, not from the cookie. The cookie just
 * helps survive the journey from landing to signup form submit.
 */

const STORAGE_KEY = 'xph_referral_code';
const CLICK_ID_KEY = 'xph_referral_click_id';
const COOKIE_NAME = 'xph_ref';
const COOKIE_DAYS = 90;

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.xpherium.com/api';

const REF_CODE_PATTERN = /^[A-Z0-9]{4,32}$/;

function safeWindow() {
  return typeof window !== 'undefined' ? window : null;
}

function readCookie(name) {
  const w = safeWindow();
  if (!w?.document?.cookie) return null;
  const all = w.document.cookie.split(';').map((c) => c.trim());
  for (const c of all) {
    if (c.startsWith(`${name}=`)) {
      try {
        return decodeURIComponent(c.slice(name.length + 1));
      } catch {
        return null;
      }
    }
  }
  return null;
}

function writeCookie(name, value, days = COOKIE_DAYS) {
  const w = safeWindow();
  if (!w?.document) return;
  const expires = new Date(Date.now() + days * 86400 * 1000).toUTCString();
  // SameSite=Lax so the cookie still rides through normal navigation while
  // blocking the obvious cross-site CSRF surface. We can't set `Secure`
  // here because the same code runs on http://localhost during development;
  // in production all serving origins are HTTPS so it doesn't matter in
  // practice.
  w.document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/; SameSite=Lax`;
}

function clearCookie(name) {
  const w = safeWindow();
  if (!w?.document) return;
  w.document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; SameSite=Lax`;
}

function normalize(code) {
  if (!code) return null;
  const v = String(code).trim().toUpperCase();
  return REF_CODE_PATTERN.test(v) ? v : null;
}

export function getStoredReferralCode() {
  try {
    const fromStorage = normalize(localStorage.getItem(STORAGE_KEY));
    if (fromStorage) return fromStorage;
  } catch {
    // ignore
  }
  const fromCookie = normalize(readCookie(COOKIE_NAME));
  return fromCookie || null;
}

export function getStoredReferralClickId() {
  try {
    const id = localStorage.getItem(CLICK_ID_KEY);
    return id && id.length > 4 ? id : null;
  } catch {
    return null;
  }
}

export function clearStoredReferral() {
  try {
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(CLICK_ID_KEY);
  } catch {
    // ignore
  }
  clearCookie(COOKIE_NAME);
}

function persistCode(code, clickId) {
  const normalized = normalize(code);
  if (!normalized) return;
  try {
    localStorage.setItem(STORAGE_KEY, normalized);
    if (clickId) localStorage.setItem(CLICK_ID_KEY, String(clickId));
  } catch {
    // ignore
  }
  writeCookie(COOKIE_NAME, normalized);
}

/**
 * Read `?ref=` from the current URL, validate it server-side, and persist.
 * Idempotent — safe to call on every render of the landing page.
 */
export async function captureReferralFromUrl() {
  const w = safeWindow();
  if (!w?.location) return null;

  let ref;
  try {
    const url = new URL(w.location.href);
    ref = url.searchParams.get('ref');
  } catch {
    return null;
  }

  const normalized = normalize(ref);
  if (!normalized) return getStoredReferralCode();

  // If we've already persisted this exact code, don't re-validate / re-click.
  const already = getStoredReferralCode();
  if (already === normalized && getStoredReferralClickId()) {
    return normalized;
  }

  try {
    const validateRes = await fetch(`${API_BASE_URL}/affiliates/validate?code=${encodeURIComponent(normalized)}`);
    const validateData = await validateRes.json().catch(() => ({}));
    if (!validateData?.valid) return getStoredReferralCode();

    let clickId = null;
    try {
      const clickRes = await fetch(`${API_BASE_URL}/affiliates/click`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          code: normalized,
          landingPath: w.location.pathname || '/',
          utmSource: new URL(w.location.href).searchParams.get('utm_source'),
          utmMedium: new URL(w.location.href).searchParams.get('utm_medium'),
          utmCampaign: new URL(w.location.href).searchParams.get('utm_campaign'),
        }),
      });
      const clickData = await clickRes.json().catch(() => ({}));
      clickId = clickData?.clickId || null;
    } catch {
      // Click logging is best-effort.
    }

    persistCode(normalized, clickId);
    return normalized;
  } catch {
    // If validation fails (network, etc.) we still persist what was given —
    // the backend will reject invalid codes at attribution time.
    persistCode(normalized, null);
    return normalized;
  }
}

/**
 * Build the partial signup payload to merge into the register POST body.
 * Returns {} if no referral is in play, so callers can spread it
 * unconditionally without polluting non-referred signups.
 */
export function getReferralSignupPayload() {
  const code = getStoredReferralCode();
  if (!code) return {};
  const clickId = getStoredReferralClickId();
  return clickId ? { referralCode: code, referralClickId: clickId } : { referralCode: code };
}

const referralAttribution = {
  captureReferralFromUrl,
  getStoredReferralCode,
  getStoredReferralClickId,
  getReferralSignupPayload,
  clearStoredReferral,
};

export default referralAttribution;
